'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { Bell, Plus, Trash2, ToggleLeft, ToggleRight, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Loader2, RefreshCw, Building2, MapPin, Filter, X, Info, Zap, Target } from 'lucide-react';
import { savedFiltersService, SavedFilter, PriceThresholdAlert, CreateSavedFilterInput } from '@/lib/services/savedFiltersService';
import { projectService, ProjectRow } from '@/lib/services/projectService';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

export const dynamic = 'force-dynamic';

const microMarkets = ['Hinjewadi', 'Kharadi', 'Baner', 'Wakad', 'Wagholi', 'Hadapsar', 'Undri', 'Viman Nagar', 'Kalyani Nagar'];
const projectTypes = ['Apartment', 'Villa', 'Plotted', 'Commercial', 'Row House'];
const projectStatuses = ['Active Sales', 'Pre-Launch', 'Nearing Possession', 'Delivered'];

function SkeletonCard() {
  return (
    <div className="card-base p-4 space-y-3 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-4 bg-muted rounded w-1/3" />
        <div className="h-6 bg-muted rounded w-16" />
      </div>
      <div className="h-3 bg-muted rounded w-2/3" />
      <div className="h-3 bg-muted rounded w-1/2" />
    </div>
  );
}

export default function AlertsPage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<'price' | 'filter'>('price');
  const [priceAlerts, setPriceAlerts] = useState<PriceThresholdAlert[]>([]);
  const [filterAlerts, setFilterAlerts] = useState<SavedFilter[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Create price alert form
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [priceForm, setPriceForm] = useState<{ project_id: string; threshold_type: 'above' | 'below'; price_per_sqft_threshold: string }>({
    project_id: '',
    threshold_type: 'above',
    price_per_sqft_threshold: '',
  });
  const [creatingPrice, setCreatingPrice] = useState(false);

  // Create filter alert form
  const [showFilterForm, setShowFilterForm] = useState(false);
  const [filterForm, setFilterForm] = useState<CreateSavedFilterInput>({
    filter_name: '',
    micro_market: '',
    project_type: '',
    min_price_sqft: undefined,
    max_price_sqft: undefined,
    project_status: '',
    email_alerts_enabled: true,
  });
  const [creatingFilter, setCreatingFilter] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) setUserId(authUser.id);
    })();
  }, []);

  const loadData = useCallback(async (uid: string) => {
    setLoading(true);
    const [pa, fa, proj] = await Promise.all([
      savedFiltersService.getPriceThresholdAlerts(uid),
      savedFiltersService.getSavedFilters(uid),
      projectService.getAllProjects(),
    ]);
    setPriceAlerts(pa);
    setFilterAlerts(fa);
    setProjects(proj);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (userId) loadData(userId);
  }, [userId, loadData]);

  const handleCreatePriceAlert = async () => {
    if (!userId || !priceForm.project_id || !priceForm.price_per_sqft_threshold) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreatingPrice(true);
    const result = await savedFiltersService.createPriceThresholdAlert(userId, {
      project_id: priceForm.project_id,
      threshold_type: priceForm.threshold_type,
      price_per_sqft_threshold: parseInt(priceForm.price_per_sqft_threshold),
    });
    setCreatingPrice(false);
    if (result) {
      toast.success('Price alert created successfully');
      setPriceAlerts((prev) => [result, ...prev]);
      setShowPriceForm(false);
      setPriceForm({ project_id: '', threshold_type: 'above', price_per_sqft_threshold: '' });
    } else {
      toast.error('Failed to create alert. It may already exist for this project.');
    }
  };

  const handleCreateFilterAlert = async () => {
    if (!userId || !filterForm.filter_name) {
      toast.error('Please enter a filter name');
      return;
    }
    setCreatingFilter(true);
    const result = await savedFiltersService.createSavedFilter(userId, filterForm);
    setCreatingFilter(false);
    if (result) {
      toast.success('Filter alert created successfully');
      setFilterAlerts((prev) => [result, ...prev]);
      setShowFilterForm(false);
      setFilterForm({ filter_name: '', micro_market: '', project_type: '', min_price_sqft: undefined, max_price_sqft: undefined, project_status: '', email_alerts_enabled: true });
    } else {
      toast.error('Failed to create filter alert');
    }
  };

  const handleDeletePriceAlert = async (id: string) => {
    setDeletingId(id);
    const ok = await savedFiltersService.deletePriceThresholdAlert(id);
    setDeletingId(null);
    if (ok) {
      toast.success('Alert deleted');
      setPriceAlerts((prev) => prev.filter((a) => a.id !== id));
    } else {
      toast.error('Failed to delete alert');
    }
  };

  const handleDeleteFilterAlert = async (id: string) => {
    setDeletingId(id);
    const ok = await savedFiltersService.deleteSavedFilter(id);
    setDeletingId(null);
    if (ok) {
      toast.success('Filter alert deleted');
      setFilterAlerts((prev) => prev.filter((a) => a.id !== id));
    } else {
      toast.error('Failed to delete filter alert');
    }
  };

  const handleTogglePriceAlert = async (id: string, current: boolean) => {
    setTogglingId(id);
    const ok = await savedFiltersService.toggleThresholdAlerts(id, !current);
    setTogglingId(null);
    if (ok) {
      setPriceAlerts((prev) => prev.map((a) => a.id === id ? { ...a, email_alerts_enabled: !current } : a));
      toast.success(!current ? 'Email alerts enabled' : 'Email alerts paused');
    }
  };

  const handleToggleFilterAlert = async (id: string, current: boolean) => {
    setTogglingId(id);
    const ok = await savedFiltersService.toggleFilterAlerts(id, !current);
    setTogglingId(null);
    if (ok) {
      setFilterAlerts((prev) => prev.map((a) => a.id === id ? { ...a, email_alerts_enabled: !current } : a));
      toast.success(!current ? 'Email alerts enabled' : 'Email alerts paused');
    }
  };

  const handleResetAlert = async (id: string) => {
    const ok = await savedFiltersService.resetTriggeredAlert(id);
    if (ok) {
      setPriceAlerts((prev) => prev.map((a) => a.id === id ? { ...a, is_triggered: false, triggered_at: undefined } : a));
      toast.success('Alert reset — monitoring resumed');
    }
  };

  const triggeredCount = priceAlerts.filter((a) => a.is_triggered).length;
  const activeFilterAlerts = filterAlerts.filter((a) => a.email_alerts_enabled).length;
  const totalAlerts = priceAlerts.length + filterAlerts.length;

  return (
    <AppLayout activeRoute="/alerts">
      {/* SEO heading */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <Bell size={22} className="text-amber-400 shrink-0" />
              Smart Price Alerts
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              AI-powered price monitoring for Pune real estate — get notified when markets move
            </p>
          </div>
          <button
            onClick={() => userId && loadData(userId)}
            className="self-start sm:self-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground text-sm transition-all"
          >
            <RefreshCw size={14} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Total Alerts', value: totalAlerts, icon: Bell, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { label: 'Triggered', value: triggeredCount, icon: AlertTriangle, color: 'text-rose-400', bg: 'bg-rose-500/10' },
            { label: 'Active Email', value: activeFilterAlerts, icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'Price Monitors', value: priceAlerts.length, icon: Target, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="card-base p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center shrink-0`}>
                <kpi.icon size={16} className={kpi.color} />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-foreground metric-value">{kpi.value}</p>
                <p className="text-[10px] text-muted-foreground truncate">{kpi.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Market insight banner */}
      <div className="card-base p-3 mb-5 flex items-start gap-3 border-amber-500/20 bg-amber-500/5">
        <Zap size={16} className="text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-amber-400">AI Market Intelligence</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pune real estate prices have risen 12–18% YoY in micro-markets like Hinjewadi and Kharadi. Set threshold alerts to capture optimal entry points before prices cross your target.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border mb-5 gap-1">
        {[
          { key: 'price', label: 'Price Threshold Alerts', count: priceAlerts.length },
          { key: 'filter', label: 'Market Filter Alerts', count: filterAlerts.length },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as 'price' | 'filter')}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium transition-all border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'text-amber-400 border-amber-500' :'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${activeTab === tab.key ? 'bg-amber-500/20 text-amber-400' : 'bg-muted text-muted-foreground'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Price Threshold Alerts Tab */}
      {activeTab === 'price' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Per-Project Price Monitors</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Get email alerts when a project's price crosses your target threshold</p>
            </div>
            <button
              onClick={() => setShowPriceForm(!showPriceForm)}
              className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg text-xs font-semibold transition-all"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">New Alert</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>

          {/* Create form */}
          {showPriceForm && (
            <div className="card-base p-4 border-amber-500/20 bg-amber-500/5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Create Price Alert</h3>
                <button onClick={() => setShowPriceForm(false)} className="p-1 rounded hover:bg-muted/60 text-muted-foreground">
                  <X size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <label className="label-base">Project *</label>
                  <select
                    value={priceForm.project_id}
                    onChange={(e) => setPriceForm((p) => ({ ...p, project_id: e.target.value }))}
                    className="input-base text-sm"
                  >
                    <option value="">Select project…</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.project_name} — {p.micro_market}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-base">Alert Type *</label>
                  <select
                    value={priceForm.threshold_type}
                    onChange={(e) => setPriceForm((p) => ({ ...p, threshold_type: e.target.value as 'above' | 'below' }))}
                    className="input-base text-sm"
                  >
                    <option value="above">Price goes above</option>
                    <option value="below">Price drops below</option>
                  </select>
                </div>
                <div>
                  <label className="label-base">Price / sqft (₹) *</label>
                  <input
                    type="number"
                    value={priceForm.price_per_sqft_threshold}
                    onChange={(e) => setPriceForm((p) => ({ ...p, price_per_sqft_threshold: e.target.value }))}
                    placeholder="e.g. 8500"
                    className="input-base text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowPriceForm(false)} className="btn-secondary text-sm py-1.5 px-3">Cancel</button>
                <button onClick={handleCreatePriceAlert} disabled={creatingPrice} className="btn-primary text-sm py-1.5 px-4 flex items-center gap-2">
                  {creatingPrice && <Loader2 size={13} className="animate-spin" />}
                  Create Alert
                </button>
              </div>
            </div>
          )}

          {/* Alert list */}
          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</div>
          ) : priceAlerts.length === 0 ? (
            <div className="card-base p-8 text-center">
              <Target size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">No price alerts yet</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Monitor specific projects and get notified when prices hit your target</p>
              <button onClick={() => setShowPriceForm(true)} className="btn-primary text-sm py-2 px-4">
                Create Your First Alert
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {priceAlerts.map((alert) => {
                const proj = alert.projects;
                const isTriggered = alert.is_triggered;
                const currentPrice = proj?.price_per_sqft ?? 0;
                const diff = currentPrice - alert.price_per_sqft_threshold;
                const diffPct = alert.price_per_sqft_threshold > 0 ? ((diff / alert.price_per_sqft_threshold) * 100).toFixed(1) : '0';
                return (
                  <div key={alert.id} className={`card-base p-4 transition-all ${isTriggered ? 'border-rose-500/40 bg-rose-500/5' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isTriggered ? 'bg-rose-500/15' : alert.threshold_type === 'above' ? 'bg-emerald-500/10' : 'bg-blue-500/10'}`}>
                          {isTriggered ? <AlertTriangle size={16} className="text-rose-400" /> : alert.threshold_type === 'above' ? <TrendingUp size={16} className="text-emerald-400" /> : <TrendingDown size={16} className="text-blue-400" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-foreground truncate">{proj?.project_name ?? 'Unknown Project'}</p>
                            {isTriggered && (
                              <span className="px-1.5 py-0.5 bg-rose-500/15 text-rose-400 text-[10px] font-semibold rounded border border-rose-500/30">TRIGGERED</span>
                            )}
                            {!alert.email_alerts_enabled && (
                              <span className="px-1.5 py-0.5 bg-muted text-muted-foreground text-[10px] rounded">Paused</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin size={10} />
                            {proj?.micro_market} · {proj?.developer_name}
                          </p>
                          <div className="flex flex-wrap gap-3 mt-2">
                            <div>
                              <p className="text-[10px] text-muted-foreground">Alert when price</p>
                              <p className="text-xs font-semibold text-foreground">
                                {alert.threshold_type === 'above' ? '↑ above' : '↓ below'} ₹{alert.price_per_sqft_threshold.toLocaleString()}/sqft
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Current price</p>
                              <p className="text-xs font-semibold text-foreground">₹{currentPrice.toLocaleString()}/sqft</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Difference</p>
                              <p className={`text-xs font-semibold ${diff > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {diff > 0 ? '+' : ''}{diff.toLocaleString()} ({diffPct}%)
                              </p>
                            </div>
                          </div>
                          {isTriggered && alert.triggered_at && (
                            <p className="text-[10px] text-rose-400 mt-1.5">
                              Triggered: {new Date(alert.triggered_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {isTriggered && (
                          <button
                            onClick={() => handleResetAlert(alert.id)}
                            title="Reset alert"
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10 transition-all"
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => handleTogglePriceAlert(alert.id, alert.email_alerts_enabled)}
                          disabled={togglingId === alert.id}
                          title={alert.email_alerts_enabled ? 'Pause email alerts' : 'Enable email alerts'}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all"
                        >
                          {togglingId === alert.id ? <Loader2 size={14} className="animate-spin" /> : alert.email_alerts_enabled ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} />}
                        </button>
                        <button
                          onClick={() => handleDeletePriceAlert(alert.id)}
                          disabled={deletingId === alert.id}
                          title="Delete alert"
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                        >
                          {deletingId === alert.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Filter Alerts Tab */}
      {activeTab === 'filter' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Market Filter Monitors</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Get notified when new projects match your saved search criteria</p>
            </div>
            <button
              onClick={() => setShowFilterForm(!showFilterForm)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-semibold transition-all"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">New Filter</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>

          {/* Create filter form */}
          {showFilterForm && (
            <div className="card-base p-4 border-blue-500/20 bg-blue-500/5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Create Filter Alert</h3>
                <button onClick={() => setShowFilterForm(false)} className="p-1 rounded hover:bg-muted/60 text-muted-foreground">
                  <X size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="label-base">Filter Name *</label>
                  <input
                    type="text"
                    value={filterForm.filter_name}
                    onChange={(e) => setFilterForm((f) => ({ ...f, filter_name: e.target.value }))}
                    placeholder="e.g. Hinjewadi 2BHK under ₹80L"
                    className="input-base text-sm"
                  />
                </div>
                <div>
                  <label className="label-base">Micro Market</label>
                  <select value={filterForm.micro_market ?? ''} onChange={(e) => setFilterForm((f) => ({ ...f, micro_market: e.target.value }))} className="input-base text-sm">
                    <option value="">Any market</option>
                    {microMarkets.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-base">Project Type</label>
                  <select value={filterForm.project_type ?? ''} onChange={(e) => setFilterForm((f) => ({ ...f, project_type: e.target.value }))} className="input-base text-sm">
                    <option value="">Any type</option>
                    {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-base">Min Price/sqft (₹)</label>
                  <input type="number" value={filterForm.min_price_sqft ?? ''} onChange={(e) => setFilterForm((f) => ({ ...f, min_price_sqft: e.target.value ? parseInt(e.target.value) : undefined }))} placeholder="e.g. 5000" className="input-base text-sm" />
                </div>
                <div>
                  <label className="label-base">Max Price/sqft (₹)</label>
                  <input type="number" value={filterForm.max_price_sqft ?? ''} onChange={(e) => setFilterForm((f) => ({ ...f, max_price_sqft: e.target.value ? parseInt(e.target.value) : undefined }))} placeholder="e.g. 12000" className="input-base text-sm" />
                </div>
                <div>
                  <label className="label-base">Project Status</label>
                  <select value={filterForm.project_status ?? ''} onChange={(e) => setFilterForm((f) => ({ ...f, project_status: e.target.value }))} className="input-base text-sm">
                    <option value="">Any status</option>
                    {projectStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <input type="checkbox" id="email-alerts" checked={filterForm.email_alerts_enabled} onChange={(e) => setFilterForm((f) => ({ ...f, email_alerts_enabled: e.target.checked }))} className="w-4 h-4 accent-amber-500" />
                  <label htmlFor="email-alerts" className="text-sm text-foreground cursor-pointer">Enable email notifications</label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowFilterForm(false)} className="btn-secondary text-sm py-1.5 px-3">Cancel</button>
                <button onClick={handleCreateFilterAlert} disabled={creatingFilter} className="btn-primary text-sm py-1.5 px-4 flex items-center gap-2">
                  {creatingFilter && <Loader2 size={13} className="animate-spin" />}
                  Save Filter
                </button>
              </div>
            </div>
          )}

          {/* Filter list */}
          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</div>
          ) : filterAlerts.length === 0 ? (
            <div className="card-base p-8 text-center">
              <Filter size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">No filter alerts yet</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Save search criteria and get notified when matching projects appear</p>
              <button onClick={() => setShowFilterForm(true)} className="btn-primary text-sm py-2 px-4">
                Create Filter Alert
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filterAlerts.map((filter) => (
                <div key={filter.id} className="card-base p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <Filter size={16} className="text-blue-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-foreground">{filter.filter_name}</p>
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${filter.email_alerts_enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                            {filter.email_alerts_enabled ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {filter.micro_market && (
                            <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground flex items-center gap-1">
                              <MapPin size={9} />{filter.micro_market}
                            </span>
                          )}
                          {filter.project_type && (
                            <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground flex items-center gap-1">
                              <Building2 size={9} />{filter.project_type}
                            </span>
                          )}
                          {(filter.min_price_sqft || filter.max_price_sqft) && (
                            <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground">
                              ₹{filter.min_price_sqft?.toLocaleString() ?? '0'}–{filter.max_price_sqft?.toLocaleString() ?? '∞'}/sqft
                            </span>
                          )}
                          {filter.project_status && (
                            <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground">{filter.project_status}</span>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2">
                          Created {new Date(filter.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {filter.last_notified_at && ` · Last notified ${new Date(filter.last_notified_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => handleToggleFilterAlert(filter.id, filter.email_alerts_enabled)}
                        disabled={togglingId === filter.id}
                        title={filter.email_alerts_enabled ? 'Pause alerts' : 'Enable alerts'}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all"
                      >
                        {togglingId === filter.id ? <Loader2 size={14} className="animate-spin" /> : filter.email_alerts_enabled ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} />}
                      </button>
                      <button
                        onClick={() => handleDeleteFilterAlert(filter.id)}
                        disabled={deletingId === filter.id}
                        title="Delete filter"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                      >
                        {deletingId === filter.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAQ Section */}
      <div className="mt-10 card-base p-5">
        <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Info size={15} className="text-blue-400" />
          Frequently Asked Questions
        </h2>
        <div className="space-y-3">
          {[
            { q: 'How quickly will I receive price alerts?', a: 'Price alerts are checked every 24 hours. When a project\'s price crosses your threshold, you\'ll receive an email notification within the same business day.' },
            { q: 'Can I set multiple alerts for the same project?', a: 'Yes — you can set one "above" and one "below" threshold per project, allowing you to monitor a price band for optimal entry and exit points.' },
            { q: 'What happens after an alert is triggered?', a: 'The alert is marked as triggered and email notifications pause. You can reset the alert to resume monitoring once you\'ve reviewed the price movement.' },
            { q: 'How do filter alerts work?', a: 'Filter alerts scan new project listings daily. When a new project matches your saved criteria (market, type, price range), you\'ll receive an email with the project details.' },
          ].map((faq, i) => (
            <div key={i} className="border-b border-border pb-3 last:border-0 last:pb-0">
              <p className="text-xs font-semibold text-foreground mb-1">{faq.q}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
